import React, { useEffect, useState } from "react";
import SharedTodoList from "../components/SharedTodoList";
import { apiFetch, jsonHeaders } from "../api/api";
import { colorForId, textColorForBgHsl } from "../utils/color";
import {
  format,
  startOfDay,
  endOfDay,
  compareAsc,
  startOfWeek,
  addDays,
  isSameDay,
} from "date-fns";
import { useNavigate } from "react-router-dom";
// Stay on dashboard for mini calendar day click; student search will navigate

function EventsTodayCard({ refreshKey = 0 }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem("token");
        const data = await apiFetch("/calendar", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const todayStart = startOfDay(new Date());
        const todayEnd = endOfDay(new Date());
        const todays = (Array.isArray(data) ? data : [])
          .filter((ev) => {
            const s = new Date(ev.startTime);
            const e = new Date(ev.endTime);
            return s <= todayEnd && e >= todayStart; // overlap with today
          })
          .sort((a, b) =>
            compareAsc(new Date(a.startTime), new Date(b.startTime))
          );
        setItems(todays);
      } catch (err) {
        setError(err.message || "Failed to load events");
      } finally {
        setLoading(false);
      }
    })();
  }, [refreshKey]);

  return (
    <div style={cardStyle}>
      <div style={cardHeader}>Events today</div>
      <div style={{ padding: "8px 12px 12px 12px", textAlign: "center" }}>
        {loading && <div style={mutedText}>Loading…</div>}
        {error && <div style={{ color: "#b91c1c" }}>Error: {error}</div>}
        {!loading && !error && items.length === 0 && (
          <div style={mutedText}>No events today</div>
        )}
        {!loading && !error && items.length > 0 && (
          <ul
            style={{
              listStyle: "none",
              padding: 0,
              margin: 0,
              display: "grid",
              gap: 8,
              justifyItems: "center",
              textAlign: "center",
            }}
          >
            {items.map((ev) => {
              const s = new Date(ev.startTime);
              const e = new Date(ev.endTime);
              const when = ev.allDay
                ? "All day"
                : `${format(s, "h:mm a")} – ${format(e, "h:mm a")}`;
              const by = ev.createdByName || "";
              const whoKey = ev.createdBy || ev.createdByName || by;
              const bg = whoKey ? colorForId(whoKey) : null;
              const fg = bg ? textColorForBgHsl(bg) : null;
              return (
                <li key={ev.eventId || ev.id} style={eventRow}>
                  <div style={{ fontWeight: 600, color: "#0f172a" }}>
                    {ev.title}
                  </div>
                  <div style={{ fontSize: 12, color: "#475569" }}>{when}</div>
                  {by && (
                    <div
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 8,
                        justifyContent: "center",
                      }}
                    >
                      <span style={{ fontSize: 12, color: "#6b7280" }}>by</span>
                      <span
                        style={{
                          fontSize: 12,
                          padding: "2px 8px",
                          borderRadius: 999,
                          background: bg || "#e5e7eb",
                          color: fg || "#111",
                          display: "inline-block",
                          minWidth: 24,
                        }}
                      >
                        {by}
                      </span>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

const cardStyle = {
  background: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: 10,
  boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
  overflow: "hidden",
};

const cardHeader = {
  padding: "10px 12px",
  borderBottom: "1px solid #e5e7eb",
  fontWeight: 700,
  color: "#111827",
  textAlign: "center",
};

const mutedText = { color: "#6b7280", fontSize: 13 };
const eventRow = {
  padding: 8,
  borderRadius: 8,
  background: "#f8fafc",
  textAlign: "center",
};

function WeekMiniCalendar({ refreshKey = 0, onDayClick }) {
  const today = new Date();
  const weekStart = startOfWeek(today, { weekStartsOn: 1 }); // Monday start
  const weekEnd = endOfDay(addDays(weekStart, 6));
  const days = Array.from({ length: 7 }).map((_, i) => addDays(weekStart, i));

  const [weekEvents, setWeekEvents] = React.useState({});
  const [error, setError] = React.useState(null);
  const [tip, setTip] = React.useState(null); // { key, x, y }

  React.useEffect(() => {
    (async () => {
      setError(null);
      try {
        const token = localStorage.getItem("token");
        const data = await apiFetch("/calendar", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const items = Array.isArray(data) ? data : [];
        // Build a map of ISO date (YYYY-MM-DD) to array of events for that day
        const map = {};
        for (const ev of items) {
          const s = new Date(ev.startTime);
          const e = new Date(ev.endTime);
          // Only consider events overlapping this week
          if (s <= weekEnd && e >= weekStart) {
            // For each day overlapped by the event within the week, add entry
            const dayCursorStart = startOfDay(s < weekStart ? weekStart : s);
            const dayCursorEnd = endOfDay(e > weekEnd ? weekEnd : e);
            for (let d = dayCursorStart; d <= dayCursorEnd; d = addDays(d, 1)) {
              const key = format(d, "yyyy-MM-dd");
              if (!map[key]) map[key] = [];
              map[key].push(ev);
            }
          }
        }
        setWeekEvents(map);
      } catch (err) {
        setError(err.message || "Failed to load week events");
      } finally {
      }
    })();
  }, [refreshKey, weekStart, weekEnd]);

  return (
    <div style={{ ...cardStyle, marginTop: 16 }}>
      <div style={cardHeader}>This week</div>
      <div style={{ padding: 12 }}>
        {error && <div style={{ color: "#b91c1c", fontSize: 12 }}>{error}</div>}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            gap: 8,
          }}
        >
          {days.map((d) => {
            const isToday = isSameDay(d, today);
            const key = format(d, "yyyy-MM-dd");
            const dayEvents = weekEvents[key] || [];
            // limit visible dots
            const maxDots = 6;
            const visible = dayEvents.slice(0, maxDots);
            const overflow = Math.max(0, dayEvents.length - visible.length);
            return (
              <div
                key={d.toISOString()}
                onClick={() => onDayClick && onDayClick(d)}
                onMouseEnter={(e) => {
                  const r = e.currentTarget.getBoundingClientRect();
                  setTip({ key, x: r.left + r.width / 2, y: r.bottom + 6 });
                }}
                onMouseLeave={() =>
                  setTip((t) => (t && t.key === key ? null : t))
                }
                style={{
                  height: 92,
                  borderRadius: 10,
                  border: isToday ? "2px solid #2d6cdf" : "1px solid #e5e7eb",
                  background: isToday ? "rgba(45,108,223,0.06)" : "#fff",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  paddingTop: 8,
                  color: "#0f172a",
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    color: isToday ? "#1e40af" : "#64748b",
                    lineHeight: 1,
                  }}
                >
                  {format(d, "EEE")}
                </div>
                <div
                  style={{
                    fontSize: 18,
                    fontWeight: 700,
                    lineHeight: 1,
                    marginTop: 2,
                  }}
                >
                  {format(d, "d")}
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: 4,
                    flexWrap: "wrap",
                    justifyContent: "center",
                    marginTop: 8,
                    maxWidth: 72,
                  }}
                >
                  {visible.map((ev) => {
                    const whoKey = ev.createdBy || ev.createdByName || ev.title;
                    const bg = colorForId(whoKey);
                    return (
                      <span
                        key={(ev.eventId || ev.id) + whoKey}
                        title={`${ev.title}${ev.allDay ? " (All day)" : ""}`}
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: 999,
                          background: bg,
                          display: "inline-block",
                        }}
                      />
                    );
                  })}
                  {overflow > 0 && (
                    <span style={{ fontSize: 10, color: "#64748b" }}>
                      +{overflow}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {tip && (weekEvents[tip.key]?.length || 0) > 0 && (
          <div
            onMouseLeave={() => setTip(null)}
            onMouseEnter={() => {}}
            style={{
              position: "fixed",
              top: tip.y,
              left: tip.x,
              transform: "translateX(-50%)",
              background: "#fff",
              border: "1px solid #e5e7eb",
              borderRadius: 10,
              boxShadow: "0 10px 24px rgba(0,0,0,0.15)",
              padding: 12,
              zIndex: 2000,
              minWidth: 240,
              maxWidth: 320,
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 8 }}>
              Events on {format(new Date(tip.key), "EEE, MMM d")}
            </div>
            <ul
              style={{
                listStyle: "none",
                padding: 0,
                margin: 0,
                display: "grid",
                gap: 6,
              }}
            >
              {weekEvents[tip.key].map((ev) => {
                const s = new Date(ev.startTime);
                const e = new Date(ev.endTime);
                const when = ev.allDay
                  ? "All day"
                  : `${format(s, "h:mm a")} – ${format(e, "h:mm a")}`;
                const whoKey = ev.createdBy || ev.createdByName || ev.title;
                const bg = colorForId(whoKey);
                return (
                  <li
                    key={(ev.eventId || ev.id) + ev.startTime}
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: 999,
                        background: bg,
                        display: "inline-block",
                      }}
                    />
                    <div style={{ display: "grid" }}>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: "#0f172a",
                        }}
                      >
                        {ev.title}
                      </div>
                      <div style={{ fontSize: 12, color: "#475569" }}>
                        {when}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function StudentQuickSearch() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [students, setStudents] = useState([]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const token = localStorage.getItem("token");
        const data = await apiFetch("/students", {
          headers: { Authorization: token ? `Bearer ${token}` : "" },
        });
        setStudents(Array.isArray(data.students) ? data.students : []);
      } catch (e) {
        setError(e.message || "Failed to load students");
        setStudents([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const norm = (s) => String(s || "").toLowerCase();
    return students
      .filter((s) => {
        const first = norm(s.firstName || s.name);
        const last = norm(s.lastName || "");
        const full = `${first} ${last}`.trim();
        const id = norm(s.studentId);
        return (
          first.includes(q) ||
          last.includes(q) ||
          full.includes(q) ||
          (q.length >= 3 && id.includes(q))
        );
      })
      .slice(0, 2);
  }, [query, students]);

  return (
    <div style={{ ...cardStyle, marginTop: 16 }}>
      <div style={cardHeader}>Find a student</div>
      <div style={{ padding: 12 }}>
        <input
          type="text"
          placeholder="Search by name or ID"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{
            width: "100%",
            padding: 12,
            fontSize: 14,
            border: "1px solid #e5e7eb",
            borderRadius: 8,
            outline: "none",
          }}
        />
        {error && (
          <div style={{ color: "#b91c1c", fontSize: 12, marginTop: 8 }}>
            {error}
          </div>
        )}
        {loading && (
          <div style={{ color: "#64748b", fontSize: 12, marginTop: 8 }}>
            Loading…
          </div>
        )}
        {query && !loading && filtered.length === 0 && (
          <div style={{ color: "#64748b", fontSize: 12, marginTop: 8 }}>
            No matches.
          </div>
        )}
        {filtered.length > 0 && (
          <ul
            style={{
              listStyle: "none",
              padding: 0,
              margin: "12px 0 0 0",
              display: "grid",
              gap: 6,
            }}
          >
            {filtered.map((s) => (
              <li
                key={s.studentId}
                onClick={() => navigate(`/students/${s.studentId}`)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: 10,
                  border: "1px solid #e5e7eb",
                  borderRadius: 8,
                  cursor: "pointer",
                  background: "#fff",
                }}
              >
                <div style={{ display: "grid" }}>
                  <div style={{ fontWeight: 600, color: "#0f172a" }}>
                    {s.firstName || s.name || ""} {s.lastName || ""}
                  </div>
                  <div style={{ fontSize: 12, color: "#64748b" }}>
                    ID: {s.studentId}
                    {s.grade ? ` · Grade ${s.grade}` : ""}
                  </div>
                </div>
                <span style={{ fontSize: 12, color: "#2563eb" }}>View</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default function NewDashboard() {
  const [firstName, setFirstName] = useState("");
  const [greetVisible, setGreetVisible] = useState(false);
  // Dashboard add-event modal state
  const [dashModalMounted, setDashModalMounted] = useState(false);
  const [dashModalVisible, setDashModalVisible] = useState(false);
  const [title, setTitle] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [allDay, setAllDay] = useState(false);
  const [formError, setFormError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    // Try to fetch basic profile for firstName; fall back to localStorage hints
    (async () => {
      try {
        const token = localStorage.getItem("token");
        const apiUrl = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
        if (!token || !apiUrl) {
          const lsName = localStorage.getItem("firstName") || "";
          setFirstName(lsName || "");
          return;
        }
        const res = await fetch(`${apiUrl}/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error("Profile fetch failed");
        const data = await res.json();
        setFirstName(data?.firstName || "");
      } catch {
        const lsName = localStorage.getItem("firstName") || "";
        setFirstName(lsName || "");
      }
    })();
  }, []);

  // Animate greeting on mount
  useEffect(() => {
    let raf = requestAnimationFrame(() => setGreetVisible(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <>
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: 32 }}>
        <h1 style={{ fontSize: 32, fontWeight: 600, margin: "32px 0 24px 0" }}>
          <span
            style={{
              display: "inline-block",
              opacity: greetVisible ? 1 : 0,
              transform: greetVisible ? "translateY(0)" : "translateY(6px)",
              transition: "opacity 420ms ease, transform 420ms ease",
            }}
          >
            {(() => {
              const h = new Date().getHours();
              const greeting =
                h < 12
                  ? "Good morning"
                  : h < 16
                  ? "Good afternoon"
                  : "Good evening";
              return `${greeting}${firstName ? `, ${firstName}` : ""}`;
            })()}
          </span>
        </h1>

        {/* Two-column layout: Shared To-Do (1/3) | Events Today (2/3) */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 2fr",
            gap: 24,
            alignItems: "start",
            marginTop: 90,
          }}
        >
          <div style={{ width: "100%", minWidth: 280 }}>
            <SharedTodoList />
          </div>
          <div style={{ minWidth: 320 }}>
            <EventsTodayCard refreshKey={refreshKey} />
            <WeekMiniCalendar
              refreshKey={refreshKey}
              onDayClick={(dateObj) => {
                // Prefill 9:00–10:00 for clicked day and open modal
                const s = new Date(
                  dateObj.getFullYear(),
                  dateObj.getMonth(),
                  dateObj.getDate(),
                  9,
                  0,
                  0,
                  0
                );
                const e = new Date(s.getTime() + 60 * 60 * 1000);
                setAllDay(false);
                setStartTime(toLocalInput(s));
                setEndTime(toLocalInput(e));
                setFormError(null);
                setDashModalMounted(true);
                requestAnimationFrame(() => setDashModalVisible(true));
              }}
            />
            <StudentQuickSearch />
          </div>
        </div>

        {/* In-dashboard Add Event Modal */}
        {dashModalMounted && (
          <div
            role="dialog"
            aria-modal="true"
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.35)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
              opacity: dashModalVisible ? 1 : 0,
              transition: "opacity 200ms ease",
            }}
            onClick={() => {
              setDashModalVisible(false);
              setTimeout(() => setDashModalMounted(false), 200);
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: "min(520px, 92vw)",
                background: "#fff",
                borderRadius: 12,
                boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
                padding: 14,
                opacity: dashModalVisible ? 1 : 0,
                transform: dashModalVisible
                  ? "translateY(0) scale(1)"
                  : "translateY(-6px) scale(0.98)",
                transition: "opacity 200ms ease, transform 200ms ease",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 8,
                }}
              >
                <div style={{ fontWeight: 700 }}>New Event</div>
                <button
                  aria-label="Close"
                  onClick={() => {
                    setDashModalVisible(false);
                    setTimeout(() => setDashModalMounted(false), 200);
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: 22,
                    lineHeight: 1,
                    cursor: "pointer",
                    color: "#64748b",
                  }}
                >
                  ×
                </button>
              </div>
              {formError && (
                <div
                  style={{
                    background: "#fee",
                    color: "#a00",
                    border: "1px solid #fbb",
                    padding: 8,
                    borderRadius: 6,
                    margin: "8px 0",
                  }}
                >
                  <strong>Error:</strong> {formError}
                </div>
              )}
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (!title || !startTime || !endTime) return;
                  try {
                    const token = localStorage.getItem("token");
                    await apiFetch("/calendar", {
                      method: "POST",
                      headers: jsonHeaders({
                        Authorization: `Bearer ${token}`,
                      }),
                      body: JSON.stringify({
                        title,
                        startTime,
                        endTime,
                        allDay,
                      }),
                    });
                    // reset and close
                    setTitle("");
                    setStartTime("");
                    setEndTime("");
                    setAllDay(false);
                    setDashModalVisible(false);
                    setTimeout(() => setDashModalMounted(false), 200);
                    setRefreshKey((k) => k + 1);
                  } catch (err) {
                    setFormError(err.message || "Failed to add event");
                  }
                }}
              >
                <div style={{ display: "grid", gap: 10 }}>
                  <input
                    style={{
                      width: "90%",
                      padding: "8px 10px",
                      border: "1px solid #ccc",
                      borderRadius: 6,
                      fontSize: 14,
                      lineHeight: 1.2,
                      maxWidth: 360,
                      margin: "0 auto",
                      display: "block",
                    }}
                    placeholder="Title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    autoFocus
                  />
                  <div
                    style={{ display: "grid", gap: 16, justifyItems: "center" }}
                  >
                    <label
                      style={{
                        display: "grid",
                        gap: 4,
                        fontSize: 12,
                        color: "#333",
                        width: "100%",
                        maxWidth: 380,
                      }}
                    >
                      Start
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "minmax(120px, 1fr) 56px minmax(140px, 1.2fr)",
                          gap: 12,
                          alignItems: "center",
                          marginTop: 6,
                          marginBottom: 6,
                        }}
                      >
                        <select
                          aria-label="Start month"
                          style={{
                            width: "100%",
                            padding: "6px 8px",
                            border: "1px solid #ccc",
                            borderRadius: 6,
                            fontSize: 14,
                            height: 50,
                            lineHeight: 1.2,
                            boxSizing: "border-box",
                          }}
                          value={parseParts(startTime).monthIndex}
                          onChange={(e) => {
                            const parts = parseParts(startTime);
                            const m = Number(e.target.value);
                            parts.monthIndex = m;
                            parts.day = clampDay(parts.year, m, parts.day);
                            const next = partsToLocalInput(parts);
                            setStartTime(next);
                            if (!allDay)
                              setEndTime(shiftLocalMinutes(next, 60));
                          }}
                        >
                          {MONTHS.map((m, i) => (
                            <option key={m} value={i}>
                              {m}
                            </option>
                          ))}
                        </select>
                        <input
                          aria-label="Start day"
                          style={{
                            width: 56,
                            padding: "6px 8px",
                            border: "1px solid #ccc",
                            borderRadius: 6,
                            fontSize: 14,
                            height: 36,
                          }}
                          type="number"
                          min={1}
                          max={31}
                          value={parseParts(startTime).day}
                          onChange={(e) => {
                            const parts = parseParts(startTime);
                            let d = Number(e.target.value || 1);
                            d = clampDay(parts.year, parts.monthIndex, d);
                            parts.day = d;
                            const next = partsToLocalInput(parts);
                            setStartTime(next);
                            if (!allDay)
                              setEndTime(shiftLocalMinutes(next, 60));
                          }}
                        />
                        {!allDay && (
                          <input
                            aria-label="Start time"
                            style={{
                              width: "100%",
                              padding: "6px 8px",
                              border: "1px solid #ccc",
                              borderRadius: 6,
                              fontSize: 14,
                              height: 36,
                              marginLeft: 20,
                            }}
                            type="time"
                            step={60}
                            value={toTimeString(parseParts(startTime))}
                            onChange={(e) => {
                              const parts = parseParts(startTime);
                              const [hh, mm] = e.target.value
                                .split(":")
                                .map(Number);
                              parts.hours = isNaN(hh) ? 0 : hh;
                              parts.minutes = isNaN(mm) ? 0 : mm;
                              const next = partsToLocalInput(parts);
                              setStartTime(next);
                              setEndTime(shiftLocalMinutes(next, 60));
                            }}
                          />
                        )}
                      </div>
                      <span
                        style={{ fontSize: 11, color: "#6b7280", marginTop: 6 }}
                      >
                        {formatWords(startTime, allDay)}
                      </span>
                    </label>
                    <label
                      style={{
                        display: "grid",
                        gap: 4,
                        fontSize: 12,
                        color: "#333",
                        width: "100%",
                        maxWidth: 380,
                      }}
                    >
                      End
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "minmax(120px, 1fr) 56px minmax(140px, 1.2fr)",
                          gap: 12,
                          alignItems: "center",
                          marginTop: 6,
                          marginBottom: 6,
                        }}
                      >
                        <select
                          aria-label="End month"
                          style={{
                            width: "100%",
                            padding: "6px 8px",
                            border: "1px solid #ccc",
                            borderRadius: 6,
                            fontSize: 14,
                            height: 50,
                            lineHeight: 1.2,
                            boxSizing: "border-box",
                          }}
                          value={parseParts(endTime).monthIndex}
                          onChange={(e) => {
                            const parts = parseParts(endTime);
                            const m = Number(e.target.value);
                            parts.monthIndex = m;
                            parts.day = clampDay(parts.year, m, parts.day);
                            const next = partsToLocalInput(parts);
                            setEndTime(next);
                            if (!allDay)
                              setStartTime(shiftLocalMinutes(next, -60));
                          }}
                        >
                          {MONTHS.map((m, i) => (
                            <option key={m} value={i}>
                              {m}
                            </option>
                          ))}
                        </select>
                        <input
                          aria-label="End day"
                          style={{
                            width: 56,
                            padding: "6px 8px",
                            border: "1px solid #ccc",
                            borderRadius: 6,
                            fontSize: 14,
                            height: 36,
                          }}
                          type="number"
                          min={1}
                          max={31}
                          value={parseParts(endTime).day}
                          onChange={(e) => {
                            const parts = parseParts(endTime);
                            let d = Number(e.target.value || 1);
                            d = clampDay(parts.year, parts.monthIndex, d);
                            parts.day = d;
                            const next = partsToLocalInput(parts);
                            setEndTime(next);
                            if (!allDay)
                              setStartTime(shiftLocalMinutes(next, -60));
                          }}
                        />
                        {!allDay && (
                          <input
                            aria-label="End time"
                            style={{
                              width: "100%",
                              padding: "6px 8px",
                              border: "1px solid #ccc",
                              borderRadius: 6,
                              fontSize: 14,
                              height: 36,
                              marginLeft: 20,
                            }}
                            type="time"
                            step={60}
                            value={toTimeString(parseParts(endTime))}
                            onChange={(e) => {
                              const parts = parseParts(endTime);
                              const [hh, mm] = e.target.value
                                .split(":")
                                .map(Number);
                              parts.hours = isNaN(hh) ? 0 : hh;
                              parts.minutes = isNaN(mm) ? 0 : mm;
                              const next = partsToLocalInput(parts);
                              setEndTime(next);
                              setStartTime(shiftLocalMinutes(next, -60));
                            }}
                          />
                        )}
                      </div>
                      <span
                        style={{ fontSize: 11, color: "#6b7280", marginTop: 6 }}
                      >
                        {formatWords(endTime, allDay)}
                      </span>
                    </label>
                  </div>
                  <label
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <input
                      type="checkbox"
                      checked={allDay}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setAllDay(checked);
                        if (!checked) {
                          const base = startTime || toLocalInput(new Date());
                          if (!startTime) setStartTime(base);
                          setEndTime(shiftLocalMinutes(base, 60));
                        }
                      }}
                    />
                    All day
                  </label>
                  <div
                    style={{
                      background: "#f8fafc",
                      border: "1px solid #eef1f6",
                      color: "#334155",
                      padding: 6,
                      borderRadius: 8,
                      fontSize: 13,
                      marginTop: 4,
                    }}
                  >
                    <strong>When:</strong>&nbsp;
                    {title ? `${title} — ` : ""}
                    {formatWords(startTime, allDay)}
                    {startTime && endTime && !allDay
                      ? ` to ${formatWords(endTime, allDay)}`
                      : ""}
                  </div>
                  <div
                    style={{
                      marginTop: 14,
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 8,
                    }}
                  >
                    <button
                      type="button"
                      style={{
                        background: "#f0f2f5",
                        color: "#333",
                        border: "1px solid #dcdfe4",
                        borderRadius: 6,
                        padding: "10px 14px",
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        setDashModalVisible(false);
                        setTimeout(() => setDashModalMounted(false), 200);
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      style={{
                        background: "#2d6cdf",
                        color: "#fff",
                        border: "none",
                        borderRadius: 6,
                        padding: "10px 14px",
                        fontWeight: 700,
                        cursor: "pointer",
                        opacity: title && startTime && endTime ? 1 : 0.6,
                      }}
                      disabled={!title || !startTime || !endTime}
                    >
                      Save
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// Helpers for date part handling and formatting (aligned with Calendar page)
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function toLocalInput(date) {
  const pad = (n) => String(n).padStart(2, "0");
  const y = date.getFullYear();
  const m = pad(date.getMonth() + 1);
  const d = pad(date.getDate());
  const h = pad(date.getHours());
  const min = pad(date.getMinutes());
  return `${y}-${m}-${d}T${h}:${min}`;
}

function formatWords(dateString, allDay) {
  if (!dateString) return "—";
  const d = new Date(dateString);
  try {
    const base = format(d, "MMMM do, yy");
    if (allDay) return base;
    return `${base} ${format(d, "h:mm a")}`;
  } catch {
    return dateString;
  }
}

function daysInMonth(year, monthIndex) {
  return new Date(year, monthIndex + 1, 0).getDate();
}
function clampDay(year, monthIndex, day) {
  const max = daysInMonth(year, monthIndex);
  if (day < 1) return 1;
  if (day > max) return max;
  return day;
}
function parseParts(localIso) {
  const now = new Date();
  const m = /^([0-9]{4})-([0-9]{2})-([0-9]{2})T([0-9]{2}):([0-9]{2})$/.exec(
    localIso || ""
  );
  if (!m) {
    return {
      year: now.getFullYear(),
      monthIndex: now.getMonth(),
      day: now.getDate(),
      hours: now.getHours(),
      minutes: now.getMinutes(),
    };
  }
  const year = Number(m[1]);
  const monthIndex = Number(m[2]) - 1;
  const day = Number(m[3]);
  const hours = Number(m[4]);
  const minutes = Number(m[5]);
  return { year, monthIndex, day, hours, minutes };
}
function partsToLocalInput(parts) {
  const pad = (n) => String(n).padStart(2, "0");
  const y = parts.year;
  const m = pad((parts.monthIndex ?? 0) + 1);
  const d = pad(parts.day ?? 1);
  const h = pad(parts.hours ?? 0);
  const min = pad(parts.minutes ?? 0);
  return `${y}-${m}-${d}T${h}:${min}`;
}
function toTimeString(parts) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(parts.hours ?? 0)}:${pad(parts.minutes ?? 0)}`;
}
function shiftLocalMinutes(localIso, minutes) {
  try {
    const p = parseParts(localIso);
    const d = new Date(p.year, p.monthIndex, p.day, p.hours, p.minutes, 0, 0);
    const shifted = new Date(d.getTime() + minutes * 60 * 1000);
    return toLocalInput(shifted);
  } catch {
    return localIso;
  }
}
